import { useRef } from 'react';
import {
  ApolloCache,
  FetchResult,
  MutationFunctionOptions,
  MutationHookOptions,
} from '@apollo/client';
import {
  PhoneticReplacementCreateMutation,
  UserProjectReplacementsDocument,
  UserProjectReplacementsQuery,
  usePhoneticReplacementCreateMutation,
} from '@replacements/graphql/operation.generated';
import logger from '@replacements/utils/phoneticLogger';
import {
  deriveDocumentBasedOnType,
  findCachedQueryByType,
  getReplacementsFromCachedQuery,
  updateCachedQueryByType,
} from '@replacements/utils/query';
import {
  Exact,
  PhoneticLibraryType,
  PhoneticReplacement,
  PhoneticReplacementInputs,
} from '../../../../server/graphql/__generated.types';

const updateReplacementHandler = (
  cache: ApolloCache<unknown>,
  libraryType: PhoneticLibraryType,
  replacement?: PhoneticReplacement
) => {
  logger.prefix = `[updateReplacementHandler]`;
  if (!replacement) {
    return;
  }
  logger.info(`Attempting to write to query: ${libraryType}`, replacement);
  const document = deriveDocumentBasedOnType(libraryType);
  const cachedQuery = findCachedQueryByType(cache, document, libraryType);
  if (!cachedQuery) {
    logger.error('cached query not found');
    return;
  }
  const replacements = getReplacementsFromCachedQuery(
    libraryType,
    document,
    cachedQuery
  );
  const newReplacements = [replacement].concat(replacements);
  const updatedQueryBody = updateCachedQueryByType(
    libraryType,
    cachedQuery,
    newReplacements
  );
  if (updatedQueryBody) {
    cache.writeQuery({ query: document, data: updatedQueryBody });
    logger.info(`succesfully wrote query body`);
    return;
  }
  logger.info(`updated query body not found: ${libraryType}`);
  logger.prefix = '';
};

const useAddPhoneticReplacement = ({
  onCompleted,
  onError,
}: MutationHookOptions<PhoneticReplacementCreateMutation>): [
  (
    args: MutationFunctionOptions<
      PhoneticReplacementCreateMutation,
      Exact<{ inputs: PhoneticReplacementInputs }>
    >
  ) => Promise<FetchResult<PhoneticReplacementCreateMutation>>,
  { loading: boolean }
] => {
  const libraryContext = useRef<PhoneticLibraryType | null>(null);
  const projectIdContext = useRef<string | null>(null);

  const [addPhoneticReplacement, { loading }] =
    usePhoneticReplacementCreateMutation({
      update: (cache, { data }) => {
        const libraryType = libraryContext.current;
        if (!libraryType) {
          logger.error(`[addPhoneticReplacement]: library type not found`);
          return;
        }
        const replacement = data?.phoneticReplacementCreate
          .replacement as PhoneticReplacement;
        updateReplacementHandler(cache, libraryType, replacement);

        const projectId = projectIdContext.current;
        if (projectId && replacement) {
          const cached = cache.readQuery<UserProjectReplacementsQuery>({
            query: UserProjectReplacementsDocument,
            variables: { inputs: { projectId } },
          });
          if (cached) {
            cache.writeQuery({
              query: UserProjectReplacementsDocument,
              variables: { inputs: { projectId } },
              data: {
                ...cached,
                user: {
                  ...cached.user,
                  project: {
                    ...cached.user.project,
                    replacements: [
                      ...(cached.user.project?.replacements ?? []),
                      replacement,
                    ],
                  },
                },
              },
            });
          }
        }
      },
      onCompleted: r => {
        if (onCompleted) {
          onCompleted(r);
        }
      },
      onError: err => {
        if (onError) {
          onError(err);
        }
      },
    });

  const onAddPhoneticReplacement = (
    args: Parameters<typeof addPhoneticReplacement>[0]
  ) => {
    if (!args?.variables?.inputs) {
      throw new Error('phoneticWrapper: no inputs provided');
    }
    // store reference to current variables since update does not
    // have proper context
    if (args.variables.inputs.libraryType) {
      libraryContext.current = args.variables.inputs.libraryType;
    }
    if (args.variables.inputs.projectId) {
      projectIdContext.current = args.variables.inputs.projectId;
    }
    return addPhoneticReplacement(args);
  };
  return [onAddPhoneticReplacement, { loading }];
};

export default useAddPhoneticReplacement;
